import React from 'react';

/**
 * Tiny inline SVG sparkline for a player's last-N match ratings.
 * Zero dependencies, zero extra network traffic — renders from values already
 * present on the Match Report list.
 *
 * Props:
 *   values: number[]   ratings in chronological order, oldest → newest (length >= 1)
 *   max: number        scale upper bound (default 10, matches rating scale)
 *   width: number      svg width in px (default 56)
 *   height: number     svg height in px (default 16)
 */
const RatingSparkline = ({ values, max = 10, width = 56, height = 16 }) => {
  if (!Array.isArray(values) || values.length === 0) return null;

  const pad = 2;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  // With only 1 point we render a lone dot at center-right.
  const pointFor = (v, i) => {
    const x =
      values.length === 1
        ? pad + innerW / 2
        : pad + (i * innerW) / (values.length - 1);
    const clamped = Math.max(0, Math.min(max, v));
    // Invert y so higher rating = higher position on screen.
    const y = pad + innerH - (clamped / max) * innerH;
    return [x, y];
  };

  const points = values.map(pointFor);
  const polyline = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  const latest = values[values.length - 1];

  // Color matches the overall rating bucket (consistent with getRatingColor)
  const strokeClass =
    latest >= 8
      ? 'stroke-emerald-500 dark:stroke-emerald-400'
      : latest >= 6
      ? 'stroke-blue-500 dark:stroke-blue-400'
      : latest >= 4
      ? 'stroke-amber-500 dark:stroke-amber-400'
      : 'stroke-red-500 dark:stroke-red-400';
  const fillClass = strokeClass.replace(/stroke-/g, 'fill-');

  const label = `Last ${values.length} rating${values.length > 1 ? 's' : ''}: ${values.join(', ')}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      className="shrink-0 opacity-90"
    >
      <title>{label}</title>
      {/* subtle baseline */}
      <line
        x1={pad}
        x2={width - pad}
        y1={height - pad}
        y2={height - pad}
        className="stroke-gray-200 dark:stroke-gray-700"
        strokeWidth="1"
      />
      {points.length > 1 && (
        <polyline
          points={polyline}
          fill="none"
          className={strokeClass}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {/* latest point dot */}
      <circle cx={last[0]} cy={last[1]} r="1.8" className={fillClass} />
    </svg>
  );
};

export default RatingSparkline;
